import { IChanges, IRow } from '@models/Forms/IForms';
import { IField, IFieldElem, IFormData } from '@models/IFormData';
import Moment from 'moment';
import { FieldValues } from 'react-hook-form';
import { IRequiredValidation } from '@models/actions/IRequiredValidation';
import { ISelectedFiles } from '@atoms/AttachFilesBlock/ISelectedFiles';

export const wrapSpecialChars = (value: string) => {
    return value.replace(/\\n/g, '\\n');
};

export const formatFieldValueByType = (field: any) => {
    switch (typeof field) {
        case 'string':
            return "'" + wrapSpecialChars(field) + "'";
        case 'number':
            return '' + field + '';
        default:
            return '' + field + '';
    }
};

export const containsValue = (fieldPath: string, data?: Record<string, IFieldElem>) => {
    return data && data[fieldPath];
};

export const getValueString = (fieldPath: string, data?: Record<string, IFieldElem>) => {
    if (data) {
        const field = data[fieldPath];
        if (field?.value !== undefined && field?.value != null) {
            if (isIsoDateString(field?.value)) {
                let val = new Date(field?.value);
                return Moment(val).format('DD.MM.YYYY HH:mm');
            } else {
                return field?.value.toString();
            }
        } else {
            return '';
        }
    }
    return '';
};

export const convertListToMap = (list: IField[], pref: string = '') => {
    const map = {} as Record<string, IFieldElem>;
    let index = 0;
    if (list) {
        for (const ele of list) {
            if (ele?.name) {
                map[pref + ele.name] = {
                    index: index,
                    name: `fields.[${index}].value`,
                    value: ele.value,
                };
                index++;
            }
        }
    }
    return map;
};

export const convertObjToMap = (object: any) => {
    const map = {} as Record<string, IFieldElem>;

    if (object) {
        const keysObj = Object.keys(object);
        for (let index = 0; index < keysObj.length; index++) {
            let keyName = keysObj[index];
            map[keyName] = {
                index: index,
                name: `fields.[${index}].value`,
                value: object[keyName],
            };
        }
    }
    return map;
};

// Выбор перечня ключей по схеме
export const getFieldKeysByScheme = (rows: IRow[], onlyEdit: boolean = false): string[] => {
    let result: string[] = [];
    if (!rows) return result;
    rows.forEach((r) => {
        r.columns.forEach((c) => {
            result = result
                .concat(c.abooks?.map((x) => x.key))
                .concat(c.abooks?.flatMap((x) => x.setValues?.sets.map((y) => y.key)))
                .concat(c.abooks?.flatMap((x) => getFieldKeysByChanges(x.changes, onlyEdit)))
                .concat(c.calcs?.map((x) => x.key))
                .concat(c.calcs?.flatMap((x) => getFieldKeysByChanges(x.changes, onlyEdit)))
                .concat(c.dicts?.map((x) => x.key))
                .concat(c.dicts?.flatMap((x) => x.setValues?.sets.map((y) => y.key)))
                .concat(c.dicts?.flatMap((x) => getFieldKeysByChanges(x.changes, onlyEdit)))
                .concat(c.fields?.map((x) => x.key))
                .concat(c.fields?.flatMap((x) => getFieldKeysByChanges(x.changes, onlyEdit)))
                .concat(c.tables?.map((x) => x.key))
                .concat(c.tables?.flatMap((x) => getFieldKeysByChanges(x.changes, onlyEdit)))
                .concat(
                    c.tables?.flatMap((x) =>
                        x.tableColumn
                            .flatMap((y) => getFieldKeysByChanges(y.changes))
                            .concat(x.tableColumnDict.flatMap((y) => getFieldKeysByChanges(y.changes, onlyEdit)))
                            .concat(x.tableColumnCalc.flatMap((y) => getFieldKeysByChanges(y.changes, onlyEdit)))
                            .concat(
                                x.tableColumnExDataSource.flatMap((y) => getFieldKeysByChanges(y.changes, onlyEdit)),
                            ),
                    ),
                )
                .concat(getFieldKeysByScheme(c.rows))
                .concat(c.blocks?.flatMap((x) => getFieldKeysByScheme(x.rows.row)))
                .concat(c.editInModals?.flatMap((x) => getFieldKeysByScheme(x.rows.row)));

            if (!onlyEdit) {
                result = result.concat(c.dicts?.flatMap((x) => x.formValues?.formValue.map((y) => y.attr)));
            }
        });
    });

    return result.filter((val) => val !== undefined);
};

// Выбор перечня ключей внутри changes
export const getFieldKeysByChanges = (changes: IChanges, onlyEdit: boolean = false): string[] => {
    let result: string[] = [];
    if (!changes) return result;
    result = changes.change.flatMap((x) =>
        x.setValues?.sets
            .map((y) => y.key)
            .concat(getFieldKeysByChanges(x.changes, onlyEdit))
            .concat(getFieldKeysByChanges(x.elseChanges?.changes, onlyEdit))
            .concat(x.setValues.addRows.map((y) => y.table))
            .concat(x.setValues.updateRows.map((y) => y.table))
            .concat(x.setValues.deleteRows.map((y) => y.table))
            .concat(x.setValues.clearTables.map((y) => y.table)),
    );
    return result;
};

// Выбор данных только для полей из схемы
export const getFormDataByScheme = (rows: IRow[], data: FieldValues, onlyEdit: boolean = false): IFormData => {
    // Набор полей из схемы
    const keys = getFieldKeysByScheme(rows, onlyEdit);
    // Возможно не оптимально по производительности
    const map = {} as Record<string, IField>;
    let list = data as IField[];
    for (const ele of list) {
        map[ele.name] = {
            name: ele.name,
            value: ele.value,
        };
    }

    return {
        fields: keys.map((k) => map[k]).filter((x) => x != undefined),
    };
};

// Заполняем пустыми значениями поля на основе схемы
export const getEmptyFormDataByScheme = (rows: IRow[]): IFormData => {
    // Набор полей из схемы
    const keys = getFieldKeysByScheme(rows);

    return {
        fields: keys.map((k) => ({
            name: k,
            value: null,
        })),
    };
};

export const canVisibility = (rules?: string, data?: Record<string, IFieldElem>) => {
    const regex = /\{.*?\}/g;
    if (rules) {
        let result = rules; // or use regex without global flag gor replacing in original var

        if (rules && data) {
            let m;

            while ((m = regex.exec(rules)) !== null) {
                // eslint-disable-next-line no-loop-func
                m.forEach((match) => {
                    let fName = match.replace('{', '').replace('}', '');
                    let fValue = data[fName];
                    let fieldVal = fValue ? formatFieldValueByType(fValue.value) : "''";
                    result = result?.replaceAll(match, fieldVal!);
                });
            }
        }
        return eval(result!);
    }
    return true;
};

export const parseTableDisplayFormat = (
    format?: string,
    data?: Record<string, IFieldElem>,
    joinSeparator: string = ', ',
) => {
    const regex = /\[.*?\]/g;
    if (format) {
        if (format && data) {
            const matches = Array.from(format.matchAll(regex)).map((match) => match[0]);
            if (matches.length > 0) {
                let match = matches[0];
                let rows: string[] = [];
                let columnName = match.replace('[', '').replace(']', '');
                let tableFieldName = format.replaceAll(match, '');
                let tableField = data[tableFieldName];
                if (tableField) {
                    let table = data[tableFieldName].value as any[];
                    if (!Array.isArray(table)) return '';
                    table.forEach((row) => {
                        rows.push(row[columnName]);
                    });
                    return rows.join(joinSeparator);
                }
            }
        }
    }
    return format;
};

export const parseDisplayFormat = (format?: string, data?: Record<string, IFieldElem>, joinSeparator: string = ',') => {
    const regex = /\{.*?\}/g;
    if (format) {
        if (format && data) {
            const matches = Array.from(format.matchAll(regex)).map((match) => match[0]);
            if (matches.length > 0) {
                let values: string[] = [];
                matches.forEach((match) => {
                    let fName = match.replace('{', '').replace('}', '');
                    if (fName.indexOf('[') != -1) {
                        // TODO Rework this
                        format = parseTableDisplayFormat(fName, data, joinSeparator);
                        if (format) {
                            values.push(format);
                        }
                    } else {
                        let fValue = data[fName];
                        let fieldVal =
                            fValue && fValue.value && fValue.value != null && fValue.value !== ''
                                ? fValue.value?.toString()
                                : '';
                        values.push(fieldVal);
                        format = format?.replaceAll(match, fieldVal!);
                    }
                });
                if (values.every((v) => v === null || v === undefined || v === '')) {
                    return { text: '', values: [] } as { text: string; values: string[] };
                }
            }
        }
        return {
            text: format.replace(/\|/g, joinSeparator),
            values: format.split('|').sort((a, b) => {
                const textA = a.toUpperCase();
                const textB = b.toUpperCase();
                return textA < textB ? -1 : textA > textB ? 1 : 0;
            }),
        };
    }

    return { text: '', values: [] } as { text: string; values: string[] };
};
export const parseDisplayFormatCell = (format?: string, data?: any) => {
    const regex = /\{.*?\}/g;
    if (format) {
        if (format && data) {
            const matches = Array.from(format.matchAll(regex)).map((match) => match[0]);
            let values: string[] = [];
            matches.forEach((match) => {
                let fName = match.replace('{', '').replace('}', '');
                let fValue = data[fName];
                let fieldVal = fValue ? fValue.toString() : '';
                values.push(fieldVal);
                format = format?.replaceAll(match, fieldVal!);
            });
            if (values.every((v) => v === null || v === undefined || v === '')) {
                return '';
            }
        }
        return format.replace(/\|/g, ',');
    }
    return '';
};

export const getValue = (fieldPath: string, data?: any) => {
    const fdata = data as IFormData;
    const field = fdata?.fields.find((obj) => {
        return obj.name === fieldPath;
    });
    if (field?.value !== undefined) {
        return field?.value;
    } else {
        return undefined;
    }
};

export const getValueBoolean = (value?: any) => {
    if (value !== undefined && value !== null) {
        return value === 1 || value === '1' ? true : false;
    } else {
        return undefined;
    }
};

const isoDateFormat = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)((-|\+(\d{2}):(\d{2})|Z)?)$/;
const isoDateNoTimeFormat = /^(\d{4})-(\d{2})-(\d{2})$/;

export const isIsoDateString = (value: any) => {
    return value && typeof value === 'string' && isoDateFormat.test(value);
};

export const isIsoDateNoTimeString = (value: any) => {
    return value && typeof value === 'string' && isoDateNoTimeFormat.test(value);
};

export const getValueDates = (value: any) => {
    if (isIsoDateString(value) || isIsoDateNoTimeString(value)) {
        return new Date(value);
    } else {
        return value;
    }
};

export const getValueTimes = (value: any) => {
    if (isIsoDateString(value)) {
        let val = new Date(value); // Moment(value).toDate();
        return val.toTimeString();
    } else {
        if (value instanceof Date) {
            let val = value as Date; // Moment(value).toDate();
            return val.toTimeString();
        } else {
            return value;
        }
    }
};

export const getDisplayValue = (value: any) => {
    if (value) {
        let str = value.trim();
        if (str === '' || str === ',') {
            return '';
        } else {
            return value;
        }
    } else {
        return value;
    }
};

export const checkAttachValidation = (conditions: IRequiredValidation[], files?: ISelectedFiles): string[] => {
    let result: string[] = [];

    if (files?.files === undefined || files?.files.length === 0) return result;

    for (let index = 0; index < conditions.length; index++) {
        let condition = conditions[index];
        let all = condition.type === undefined || condition.type === 'all';

        for (let index = 0; index < files?.attachProperties.length; index++) {
            const element = files?.attachProperties[index];
            let map = convertObjToMap(element);
            let checkResult = canVisibility(condition.condition, map);
            if (!checkResult && all) {
                result.push(condition.message);
                break;
            }
            if (checkResult && !all) {
                break;
            }
            if (!checkResult && !all && index === files?.attachProperties.length - 1) {
                result.push(condition.message);
            }
        }
    }
    return result;
};
